import { Link, useNavigate } from '@tanstack/react-router';
import type { CSSProperties, ReactNode } from 'react';
import {
  EMPLOYMENT_TYPE_LABELS,
  formatClockRange,
  JOB_BUILDER_SECTION_LABELS,
  openBlockers,
  SHIFT_DISPLAY_ZONES,
  WORK_MODE_LABELS,
  type JobDetail,
  type JobOverview,
} from '@nest-recruit/shared';
import { Card, MetricStrip } from '../../../../components/Card';
import { SkeletonLines } from '../../../../components/Skeleton';
import { ServerErrorState } from '../../../../components/system-states/SystemState';
import { EMPTY, formatIsoDate, formatNumber } from '../../../../lib/format';
import { useModule } from '../../../../lib/modules';
import { useCanEdit, useCanView } from '../../../../stores/session';
import { CustomFieldsCard } from '../../../admin/components/CustomFieldsCard';
import { useJobOverview } from '../../api/publishing-api';
import styles from './hub.module.css';

/**
 * JOB-10's overview, in JOB-12's ⋯ menu since R2: the config summary, where applicants
 * come from, and slots filled (E3 "job opening detail"). The stage bar above is the
 * pipeline snapshot now (PRD §9). Counts only — who they are is S14's candidate screens,
 * and the analytics behind the sources are S29's.
 */
export function OverviewTab({ detail }: { readonly detail: JobDetail }): ReactNode {
  const overview = useJobOverview(detail.id);
  const canEditJob = useCanEdit('job_opening');
  const navigate = useNavigate();
  const isCareerSiteOn = useModule('career_site');
  const isRequestsOn = useModule('requisitions');
  const canSeePostings = useCanView('posting') && isCareerSiteOn;

  if (overview.isPending) return <SkeletonLines count={6} />;
  if (overview.error) return <ServerErrorState onRetry={() => void overview.refetch()} />;
  const data = overview.data;
  const toPostings = canSeePostings
    ? () => void navigate({ to: '/jobs/$jobId/setup/$section', params: { jobId: detail.id, section: 'form' } })
    : undefined;

  return (
    <div className={styles.overview}>
      {detail.status === 'draft' && <SetupNotice detail={detail} />}
      <MetricStrip
        metrics={[
          { label: 'Applicants', value: formatNumber(data.totalApplicants) },
          { label: 'This week', value: formatNumber(data.applicantsThisWeek) },
          ...(isCareerSiteOn ? [{ label: 'Open form links', value: formatNumber(data.livePostings), ...(toPostings ? { onClick: toPostings } : {}) }] : []),
          { label: 'Seats filled', value: `${detail.slotsFilled} of ${detail.slotsTotal}` },
        ]}
      />
      <div className={styles.overviewGrid}>
        <Card title="Where applicants come from" meta="Every application carries its source">
          <Sources overview={data} isCareerSiteOn={isCareerSiteOn} />
        </Card>
        <Card title="About this job">
          <Facts detail={detail} isRequestsOn={isRequestsOn} />
        </Card>
        <CustomFieldsCard module="job_opening" entityId={detail.id} canEdit={canEditJob} />
      </div>
    </div>
  );
}

function SetupNotice({ detail }: { readonly detail: JobDetail }): ReactNode {
  const unfinished = openBlockers(detail.sections);
  const first = unfinished[0]?.section ?? 'basics';
  return (
    <p className={styles.notice}>
      This job is still being set up
      {unfinished.length > 0 ? `: ${unfinished.length} ${unfinished.length === 1 ? 'section' : 'sections'} to finish` : ''}.{' '}
      <Link to="/jobs/$jobId/setup/$section" params={{ jobId: detail.id, section: first }}>
        Continue with {JOB_BUILDER_SECTION_LABELS[first].toLowerCase()}
      </Link>
    </p>
  );
}

function Sources({ overview, isCareerSiteOn }: { readonly overview: JobOverview; readonly isCareerSiteOn: boolean }): ReactNode {
  if (overview.bySource.length === 0) {
    return <p className={styles.muted}>No applications yet. {isCareerSiteOn ? 'Publish the job, and each' : 'Each'} channel's applicants are counted here.</p>;
  }
  const largest = Math.max(...overview.bySource.map((source) => source.count));
  return (
    <div className={styles.sources}>
      <ul className={styles.sourceList}>
        {overview.bySource.map((source) => (
          <li key={source.label} className={styles.source}>
            <span className={styles.sourceLabel}>{source.label}</span>
            <span className={styles.sourceTrack} aria-hidden>
              {/* Measured geometry, so inline (S2 convention): the bar's length is the count itself. */}
              <span className={styles.sourceBar} style={barStyle(source.count, largest)} />
            </span>
            <span className={styles.sourceCount}>{formatNumber(source.count)}</span>
          </li>
        ))}
      </ul>
      {overview.needsSourceReview > 0 && (
        <p className={styles.muted}>
          {overview.needsSourceReview} {overview.needsSourceReview === 1 ? 'application arrived' : 'applications arrived'} without a
          tracked link, so {overview.needsSourceReview === 1 ? 'its' : 'their'} source is marked for review.
        </p>
      )}
    </div>
  );
}

function Facts({ detail, isRequestsOn }: { readonly detail: JobDetail; readonly isRequestsOn: boolean }): ReactNode {
  const zone = SHIFT_DISPLAY_ZONES.find((option) => option.value === detail.shiftDisplayZone)?.label;
  const shift =
    detail.shiftStart && detail.shiftEnd
      ? `${formatClockRange(detail.shiftStart, detail.shiftEnd)} BD time${zone ? ` · shown in ${zone} time too` : ''}`
      : (detail.shiftHours ?? EMPTY);
  const rows: readonly (readonly [string, string])[] = [
    ['Type', `${EMPLOYMENT_TYPE_LABELS[detail.employmentType]} · ${WORK_MODE_LABELS[detail.workMode]}`],
    ['Location', detail.location ?? EMPTY],
    ['Shift', shift],
    ['Target close', formatIsoDate(detail.targetCloseDate)],
    ['Earliest start', formatIsoDate(detail.targetStartDate)],
    // D67 (ADR 0088): the request's latest start date comes with the job.
    ['Latest start', formatIsoDate(detail.targetStartLatest)],
    ['Opened', detail.openedAt ? formatIsoDate(detail.openedAt.slice(0, 10)) : EMPTY],
    ...(isRequestsOn ? [['Requisition', detail.requisitionReference ?? EMPTY] as const] : []),
  ];
  return (
    <dl className={styles.facts}>
      {rows.map(([label, value]) => (
        <div key={label} className={styles.fact}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

const PERCENT = 100;

function barStyle(count: number, largest: number): CSSProperties {
  return { inlineSize: `${Math.round((count / largest) * PERCENT)}%` };
}
